/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { metrics } from 'react-metrics';
import { Provider } from 'react-redux';
import HTML5Backend from 'react-dnd-html5-backend';
import { DragDropContextProvider } from 'react-dnd';
import { ApolloProvider as ApolloProviderOriginal } from '@apollo/client';
import { ConnectedRouter } from 'connected-react-router/immutable';
import { inspect } from '@xstate/inspect';

import 'sanitize.css/sanitize.css';
// Load the favicon
import './favicon.ico';

import { apolloClient } from 'entities/graphql';
import history from 'utils/history';
import App from 'containers/App';
import Wrapper from 'containers/Wrapper';
import { config as metricsConfig, options as metricsOptions } from 'metrics';
import { NonprofitOnboardingFlow } from 'components/Flow/flows/nonprofitOnboarding';
import config from 'config';

import configureStore from './store';
// Import CSS reset and Global Styles
import GlobalStyle from './global-styles';
// Import routes
import Routes from './routes';
import { PayPalVerificationCodeProvider } from './PayPalVerificationCodeProvider';
import { setReduxStoreReference } from './components/ImpactAiDrawer/hooks/methods/redux';

if (!config.isProduction && localStorage.getItem('x-debug')) {
  inspect({
    iframe: false,
  });
}

// Create the redux store with history
// this uses the singleton browserHistory provided by react-router
// Optionally, this could be changed to leverage a created history
// e.g. `const browserHistory = useRouterHistory(createBrowserHistory)();`
const initialState = {};
const store = configureStore(initialState, history);

// Set store reference for ImpactAI redux method
setReduxStoreReference(store);

// eslint-disable-next-line react/prop-types
const ApolloProvider = ({ children }) => (
  <ApolloProviderOriginal client={apolloClient}>{children}</ApolloProviderOriginal>
);

const Root = () => (
  <>
    <GlobalStyle />
    <DragDropContextProvider backend={HTML5Backend}>
      <Provider store={store}>
        <ApolloProvider>
          <ConnectedRouter history={history}>
            <App>
              <PayPalVerificationCodeProvider>
                <Wrapper>
                  <NonprofitOnboardingFlow />
                  <Routes />
                </Wrapper>
              </PayPalVerificationCodeProvider>
            </App>
          </ConnectedRouter>
        </ApolloProvider>
      </Provider>
    </DragDropContextProvider>
  </>
);

const MetricsDecoratedRoot = metrics(metricsConfig, metricsOptions)(Root);

ReactDOM.render(<MetricsDecoratedRoot />, document.getElementById('app'));
